import React, { useMemo, useState, useEffect } from "react";
import { Box, Alert } from "@mui/material";
import ContractTermsForm, { amountFields } from "./components/ContractTermsForm";
import CashflowScheduleReview from "./components/CashflowScheduleReview";
import IrregularAmortisationView from "./components/IrregularAmortisationView";
import {
  buildAmortisation,
  buildCashflows,
  calculateMonthlyIrr,
  COVERAGE_ERROR,
  formatAmountEntry,
  fromDateString,
  parseAmount,
  validateInputs,
} from "./utils/calculator";
import { groupByYA } from "../utils/dateUtils";
import { processYARounding } from "../utils/rounding";
import RepaymentLoading from "../components/RepaymentLoading";
import FinalView from "../components/FinalView";

const emptyForm = {
  cost: "",
  deposit: "",
  recurringCount: "",
  recurringAmount: "",
  finalAmount: "",
  firstDate: "",
};

/** Convert irregular amortisation rows to the HP schedule shape used by CA grouping. */
const toHpSchedule = (amortisation) =>
  amortisation.map((row) => ({
    instalmentNo: row.period,
    dueDate: fromDateString(row.dueDate),
    openingBalance: row.opening,
    principal: row.principal,
    interest: row.interest,
    instalment: row.instalment,
    closingBalance: row.closing,
  }));

const IrregularPaymentsTool = ({
  onAddToHPSchedule,
  caTypeOptions,
  caSettings,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");

  const [step1Complete, setStep1Complete] = useState(false);
  const [step1Inputs, setStep1Inputs] = useState(null);
  const [cashflowSchedule, setCashflowSchedule] = useState([]);

  const [step2Complete, setStep2Complete] = useState(false);
  const [results, setResults] = useState(null);
  const [hpSchedule, setHpSchedule] = useState(null);

  const [step3Complete, setStep3Complete] = useState(false);
  const [step3Results, setStep3Results] = useState(null);
  const [isRepaymentLoading, setIsRepaymentLoading] = useState(false);
  const [loadingStartTime, setLoadingStartTime] = useState(null);

  const parsed = useMemo(
    () => ({
      cost: parseAmount(form.cost),
      deposit: parseAmount(form.deposit || "0"),
      recurringCount: Number(form.recurringCount),
      recurringAmount: parseAmount(form.recurringAmount),
      finalAmount: parseAmount(form.finalAmount),
      firstDate: form.firstDate,
    }),
    [form]
  );

  const handleFormChange = (event) => {
    const { name } = event.target;
    let { value } = event.target;

    if (amountFields.has(name)) {
      const rawValue = value.replace(/,/g, "");
      if (!/^\d*(\.\d{0,2})?$/.test(rawValue)) return;
      value = formatAmountEntry(rawValue);
    } else if (name === "recurringCount") {
      if (value !== "" && !/^\d+$/.test(value)) return;
    }

    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    setNotice("");
  };

  const handleDateChange = (dateString) => {
    setForm((current) => ({ ...current, firstDate: dateString }));
    setErrors((current) => ({ ...current, firstDate: undefined }));
    setNotice("");
  };

  const handleGenerateSchedule = (event) => {
    event.preventDefault();
    const nextErrors = validateInputs(parsed);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setNotice("Please correct the highlighted fields.");
      return;
    }

    setCashflowSchedule(buildCashflows(parsed));
    setStep1Inputs({
      assetCost: parsed.cost,
      deposit: parsed.deposit || 0,
      recurringCount: parsed.recurringCount,
      recurringAmount: parsed.recurringAmount,
      finalAmount: parsed.finalAmount,
      firstDate: parsed.firstDate,
      inAdvance: false,
    });
    setStep1Complete(true);
    setStep2Complete(false);
    setResults(null);
    setHpSchedule(null);
    setStep3Complete(false);
    setStep3Results(null);
    setNotice("");
  };

  const updatePayment = (index, rawValue) => {
    const unformatted = rawValue.replace(/,/g, "");
    if (!/^\d*(\.\d{0,2})?$/.test(unformatted)) return;

    setCashflowSchedule((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index
          ? { ...row, amount: formatAmountEntry(unformatted) }
          : row
      )
    );
    setNotice("");
  };

  const handleConfirmSchedule = () => {
    try {
      const normalised = cashflowSchedule.map((row, index) => ({
        ...row,
        amount: index === 0 ? Number(row.amount) : parseAmount(row.amount),
      }));

      if (normalised.some((row, index) => index > 0 && !(row.amount > 0))) {
        throw new Error(COVERAGE_ERROR);
      }

      const monthlyIrr = calculateMonthlyIrr(normalised);
      const amortisation = buildAmortisation(normalised, monthlyIrr);
      const schedule = toHpSchedule(amortisation);

      setCashflowSchedule(normalised);
      setResults({ monthlyIrr, amortisation });
      setHpSchedule(schedule);
      setStep2Complete(true);
      setStep3Complete(false);
      setStep3Results(null);
      setNotice("");
    } catch (error) {
      setResults(null);
      setHpSchedule(null);
      setNotice(`Please check the following errors:\n${error.message}`);
    }
  };

  const handleBackToForm = () => {
    setStep1Complete(false);
    setStep2Complete(false);
    setResults(null);
    setHpSchedule(null);
    setStep3Complete(false);
    setStep3Results(null);
    setNotice("");
  };

  const handleBackToCashflow = () => {
    setStep2Complete(false);
    setResults(null);
    setHpSchedule(null);
    setStep3Complete(false);
    setStep3Results(null);
    setNotice("");
  };

  const handleStep3Submit = (inputs) => {
    setIsRepaymentLoading(true);
    setLoadingStartTime(Date.now());
    setStep3Results({ inputs });
    setStep3Complete(true);
  };

  const handleCancel = () => {
    setStep3Complete(false);
    setStep3Results(null);
    setIsRepaymentLoading(false);
    setLoadingStartTime(null);
  };

  useEffect(() => {
    if (isRepaymentLoading && loadingStartTime && step3Complete) {
      const elapsed = Date.now() - loadingStartTime;
      const remainingTime = Math.max(0, 1000 - elapsed);

      if (remainingTime > 0) {
        const timer = setTimeout(() => {
          setIsRepaymentLoading(false);
          setLoadingStartTime(null);
        }, remainingTime);
        return () => clearTimeout(timer);
      }

      setIsRepaymentLoading(false);
      setLoadingStartTime(null);
    }
  }, [isRepaymentLoading, loadingStartTime, step3Complete]);

  const step3ComputedResults = useMemo(() => {
    if (!step3Results || !hpSchedule || !step1Inputs) return null;

    const { inputs } = step3Results;
    const yaGroups = groupByYA(
      hpSchedule,
      inputs.fyEndMonth,
      step1Inputs.inAdvance
    );
    const { roundedGroups, adjustment } = processYARounding(
      yaGroups,
      step1Inputs.assetCost
    );

    return {
      roundedGroups,
      adjustment,
      originalTotal: adjustment.originalTotal,
      adjustedTotal: adjustment.adjustedTotal,
    };
  }, [step3Results, hpSchedule, step1Inputs]);

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setNotice("");
    setStep1Complete(false);
    setStep1Inputs(null);
    setCashflowSchedule([]);
    setStep2Complete(false);
    setResults(null);
    setHpSchedule(null);
    setStep3Complete(false);
    setStep3Results(null);
    setIsRepaymentLoading(false);
    setLoadingStartTime(null);
  };

  const isErrorNotice =
    notice.includes("errors") || notice.includes("correct");

  return (
    <Box>
      {!step1Complete ? (
        <Box>
          <ContractTermsForm
            form={form}
            errors={errors}
            onChange={handleFormChange}
            onDateChange={handleDateChange}
            onSubmit={handleGenerateSchedule}
          />
          {notice && (
            <Alert
              severity={isErrorNotice ? "error" : "info"}
              sx={{ mt: 2, borderRadius: 2, whiteSpace: "pre-line" }}
            >
              {notice}
            </Alert>
          )}
        </Box>
      ) : !step2Complete ? (
        <Box>
          <CashflowScheduleReview
            schedule={cashflowSchedule}
            onUpdatePayment={updatePayment}
            onConfirm={handleConfirmSchedule}
            onBack={handleBackToForm}
          />
          {notice && (
            <Alert
              severity={isErrorNotice ? "error" : "warning"}
              sx={{ mt: 2, borderRadius: 2, whiteSpace: "pre-line" }}
            >
              {notice}
            </Alert>
          )}
        </Box>
      ) : isRepaymentLoading ? (
        <RepaymentLoading />
      ) : !step3Complete ? (
        <IrregularAmortisationView
          results={results}
          schedule={hpSchedule}
          step1Inputs={step1Inputs}
          caTypeOptions={caTypeOptions}
          caSettings={caSettings}
          onBack={handleBackToCashflow}
          onStep3Submit={handleStep3Submit}
        />
      ) : (
        <FinalView
          schedule={hpSchedule}
          step1Inputs={step1Inputs}
          step2Results={step3Results}
          step2ComputedResults={step3ComputedResults}
          onCancel={handleCancel}
          onAddToHPSchedule={onAddToHPSchedule}
          onReset={handleReset}
        />
      )}
    </Box>
  );
};

export default IrregularPaymentsTool;
